import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Star, Moon, Calendar as CalendarIcon, AlertTriangle, Check } from 'lucide-react';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { useI18n } from '../context/I18nContext';

const MilkyWayPlanner = ({ latitude, longitude }) => {
  const { t } = useI18n();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Calculate Milky Way visibility
  const calculateMilkyWayData = (date, lat, lng) => {
    const month = date.getMonth();
    const dayOfYear = Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 86400000);
    
    // Milky Way core is best visible March-October in Northern Hemisphere
    // and September-March in Southern Hemisphere
    const isNorthern = lat >= 0;
    
    let seasonScore = 0;
    if (isNorthern) {
      // Best: June-August, Good: April-May, Sept-Oct
      if (month >= 5 && month <= 7) seasonScore = 100;
      else if (month >= 3 && month <= 4) seasonScore = 70;
      else if (month >= 8 && month <= 9) seasonScore = 60;
      else seasonScore = 20;
    } else {
      // Southern: Best Feb-April, Aug-Oct
      if (month >= 1 && month <= 3) seasonScore = 100;
      else if (month >= 7 && month <= 9) seasonScore = 90;
      else seasonScore = 40;
    }

    // Moon phase calculation
    const reference = new Date(2000, 0, 6, 18, 14, 0);
    const daysSince = (date - reference) / 86400000;
    const lunarCycle = 29.530588853;
    const moonPhase = (daysSince % lunarCycle) / lunarCycle;
    const moonIllumination = Math.round((1 - Math.cos(2 * Math.PI * moonPhase)) / 2 * 100);
    
    // Moon interference score (less moon = better)
    const moonScore = 100 - moonIllumination;

    // Galactic center approximate position
    // RA: 17h 45m, Dec: -29°
    // Best visible when it's high in the sky after astronomical twilight
    const gcDec = -29;
    const latRad = lat * Math.PI / 180;
    const decRad = gcDec * Math.PI / 180;
    
    // Maximum altitude of galactic center
    const maxAlt = 90 - Math.abs(lat - gcDec);
    const altitudeScore = Math.max(0, Math.min(100, (maxAlt / 90) * 100));

    // Calculate best viewing time (approximate)
    // GC transits around midnight in June at most longitudes
    const monthOffset = (month - 5) * 2; // hours offset from June
    const transitHour = (0 - monthOffset + 24) % 24; // midnight in June
    
    // Overall visibility score
    const overallScore = Math.round(
      (seasonScore * 0.4 + moonScore * 0.4 + altitudeScore * 0.2)
    );

    // Best dates this month (new moon ± 5 days)
    const nextNewMoon = new Date(date);
    const daysToNewMoon = Math.round((1 - moonPhase) * lunarCycle);
    nextNewMoon.setDate(nextNewMoon.getDate() + daysToNewMoon);

    return {
      date: date,
      seasonScore,
      moonScore,
      moonIllumination,
      altitudeScore,
      maxAltitude: maxAlt,
      overallScore,
      transitHour,
      recommendation: overallScore >= 70 ? 'Excellent' : 
                      overallScore >= 50 ? 'Good' : 
                      overallScore >= 30 ? 'Fair' : 'Poor',
      nextNewMoon,
      gcAzimuth: isNorthern ? 180 : 0, // South in N hemisphere, North in S
      bestTime: `${Math.floor(transitHour).toString().padStart(2, '0')}:00 - ${Math.floor((transitHour + 4) % 24).toString().padStart(2, '0')}:00`
    };
  };

  const milkyWayData = useMemo(() => 
    calculateMilkyWayData(selectedDate, latitude, longitude),
    [selectedDate, latitude, longitude]
  );

  // Generate month calendar data
  const monthData = useMemo(() => {
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start, end });
    
    return days.map(day => ({
      date: day,
      ...calculateMilkyWayData(day, latitude, longitude)
    }));
  }, [selectedDate, latitude, longitude]);

  // Find best nights this month
  const bestNights = useMemo(() => {
    return monthData
      .filter(d => d.overallScore >= 60)
      .sort((a, b) => b.overallScore - a.overallScore)
      .slice(0, 5);
  }, [monthData]);

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-emerald-500';
    if (score >= 50) return 'text-amber-500';
    if (score >= 30) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 70) return 'bg-emerald-500/20 border-emerald-500/30';
    if (score >= 50) return 'bg-amber-500/20 border-amber-500/30';
    if (score >= 30) return 'bg-orange-500/20 border-orange-500/30';
    return 'bg-red-500/20 border-red-500/30';
  };

  const getRecommendationLabel = (recommendation) => {
    switch (recommendation) {
      case 'Excellent': return t('shotScore.excellent');
      case 'Good': return t('shotScore.good');
      case 'Fair': return t('shotScore.fair');
      case 'Poor': return t('shotScore.poor');
      default: return recommendation;
    }
  };

  return (
    <div className="space-y-4">
      {/* Main Card */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-400" />
              {t('milkyWay.title')}
            </CardTitle>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-white/10 bg-slate-800" data-testid="mw-date-picker">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(selectedDate, 'MMM d, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-white/10">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setCalendarOpen(false);
                    }
                  }}
                  className="rounded-md"
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardContent>
          {/* Visibility Score */}
          <div className={`rounded-lg p-4 border ${getScoreBg(milkyWayData.overallScore)} mb-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 mb-1">{t('milkyWay.visibility')}</p>
                <p className={`text-4xl font-mono font-bold ${getScoreColor(milkyWayData.overallScore)}`}>
                  {milkyWayData.overallScore}%
                </p>
                <p className={`text-sm font-medium ${getScoreColor(milkyWayData.overallScore)}`}>
                  {getRecommendationLabel(milkyWayData.recommendation)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{t('milkyWay.bestTime')}</p>
                <p className="font-mono text-white text-lg">{milkyWayData.bestTime}</p>
                <p className="text-xs text-slate-500">{latitude >= 0 ? t('milkyWay.lookSouth') : t('milkyWay.lookNorth')}</p>
              </div>
            </div>
          </div>

          {/* Score Breakdown */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">{t('milkyWay.season')}</p>
              <p className={`font-mono text-lg ${getScoreColor(milkyWayData.seasonScore)}`}>
                {milkyWayData.seasonScore}%
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">{t('milkyWay.moon')}</p>
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-slate-400" />
                <p className={`font-mono text-lg ${getScoreColor(milkyWayData.moonScore)}`}>
                  {milkyWayData.moonIllumination}%
                </p>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">{t('milkyWay.maxAltitude')}</p>
              <p className="font-mono text-lg text-white">
                {milkyWayData.maxAltitude.toFixed(0)}°
              </p>
            </div>
          </div>

          {/* Warnings/Tips */}
          {milkyWayData.moonIllumination > 50 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
              <div>
                <p className="text-sm text-amber-500 font-medium">{t('milkyWay.moonInterference')}</p>
                <p className="text-xs text-slate-400">
                  {t('milkyWay.moonIs')} {milkyWayData.moonIllumination}% {t('moon.illuminated')}. {t('milkyWay.considerWaiting')}{' '}
                  {format(milkyWayData.nextNewMoon, 'MMM d')} {t('milkyWay.newMoonParen')}
                </p>
              </div>
            </div>
          )}

          {milkyWayData.overallScore >= 70 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <Check className="w-4 h-4 text-emerald-500 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-500 font-medium">{t('milkyWay.greatNight')}</p>
                <p className="text-xs text-slate-400">
                  {t('milkyWay.excellentConditions')}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Best Nights This Month */}
      <Card className="bg-slate-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading text-slate-300">
            {t('milkyWay.bestNights')} - {format(selectedDate, 'MMMM')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {bestNights.length === 0 ? (
            <p className="text-sm text-slate-500">{t('milkyWay.noIdealNights')}</p>
          ) : (
            <div className="space-y-2">
              {bestNights.map((night, i) => (
                <motion.div
                  key={night.date.toISOString()}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center justify-between p-2 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors cursor-pointer"
                  onClick={() => setSelectedDate(night.date)}
                  data-testid={`best-night-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getScoreBg(night.overallScore)}`}>
                      <Star className={`w-4 h-4 ${getScoreColor(night.overallScore)}`} />
                    </div>
                    <div>
                      <p className="text-sm text-white font-medium">
                        {format(night.date, 'EEE, MMM d')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t('milkyWay.moon')}: {night.moonIllumination}%
                      </p>
                    </div>
                  </div>
                  <div className={`font-mono text-lg ${getScoreColor(night.overallScore)}`}>
                    {night.overallScore}%
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MilkyWayPlanner;
